/**
 * Loyalty enum → label dictionaries.
 *
 * NOTE: every value below is an i18next KEY (e.g. 'loyalty:labels.cardStatus.active'), not
 * literal display text — consumers must render `t(dict[value])`. This keeps the existing
 * lookup ergonomic (still a plain object, easy to index) while deferring the actual string to
 * render time, so it follows the active language. Translations live in
 * public/locales/{lng}/loyalty.json under the `labels` key.
 */
import type {
  BonusAccountStatus,
  BonusTransactionType,
  CardPoolStatus,
  GenerateMode,
  LoyaltyCardStatus,
  LoyaltyCardType,
  LoyaltyProgramModel,
  LoyaltyRuleType,
  ReservationStatus,
} from './types';

/** i18next keys — render with `t(BONUS_STATUS_LABELS[status])`. */
export const BONUS_STATUS_LABELS: Record<BonusAccountStatus, string> = {
  ACTIVE: 'loyalty:labels.bonusStatus.active',
  BLOCKED: 'loyalty:labels.bonusStatus.blocked',
  CLOSED: 'loyalty:labels.bonusStatus.closed',
};

/** i18next keys — render with `t(CARD_STATUS_LABELS[status])`. */
export const CARD_STATUS_LABELS: Record<LoyaltyCardStatus, string> = {
  ACTIVE: 'loyalty:labels.cardStatus.active',
  BLOCKED: 'loyalty:labels.cardStatus.blocked',
  EXPIRED: 'loyalty:labels.cardStatus.expired',
};

/** i18next keys — render with `t(CARD_TYPE_LABELS[type])`. */
export const CARD_TYPE_LABELS: Record<LoyaltyCardType, string> = {
  PHYSICAL: 'loyalty:labels.cardType.physical',
  VIRTUAL: 'loyalty:labels.cardType.virtual',
};

/** i18next keys — render with `t(TX_TYPE_LABELS[type])`. */
export const TX_TYPE_LABELS: Record<BonusTransactionType, string> = {
  ACCRUAL: 'loyalty:labels.txType.accrual',
  REDEMPTION: 'loyalty:labels.txType.redemption',
  ADJUSTMENT: 'loyalty:labels.txType.adjustment',
  EXPIRATION: 'loyalty:labels.txType.expiration',
};

/** i18next keys — render with `t(RULE_TYPE_LABELS[type])`. */
export const RULE_TYPE_LABELS: Record<LoyaltyRuleType, string> = {
  ACCRUAL: 'loyalty:labels.ruleType.accrual',
  REDEMPTION: 'loyalty:labels.ruleType.redemption',
  TIER_ASSIGNMENT: 'loyalty:labels.ruleType.tierAssignment',
};

/** i18next keys — render with `t(CARD_POOL_STATUS_LABELS[status])`. */
export const CARD_POOL_STATUS_LABELS: Record<CardPoolStatus, string> = {
  AVAILABLE: 'loyalty:labels.cardPoolStatus.available',
  ISSUED: 'loyalty:labels.cardPoolStatus.issued',
};

/** i18next keys — render with `t(PROGRAM_MODEL_LABELS[model])`. */
export const PROGRAM_MODEL_LABELS: Record<LoyaltyProgramModel, string> = {
  BONUS: 'loyalty:labels.programModel.bonus',
  DISCOUNT: 'loyalty:labels.programModel.discount',
  NONE: 'loyalty:labels.programModel.none',
};

/** i18next keys — render with `t(GENERATE_MODE_LABELS[mode])`. */
export const GENERATE_MODE_LABELS: Record<GenerateMode, string> = {
  RANGE: 'loyalty:labels.generateMode.range',
  MASK: 'loyalty:labels.generateMode.mask',
};

/** i18next keys — render with `t(RESERVATION_STATUS_LABELS[status])`. */
export const RESERVATION_STATUS_LABELS: Record<ReservationStatus, string> = {
  RESERVED: 'loyalty:labels.reservationStatus.reserved',
  CONFIRMED: 'loyalty:labels.reservationStatus.confirmed',
  RELEASED: 'loyalty:labels.reservationStatus.released',
};

export const CARD_TYPE_OPTIONS: LoyaltyCardType[] = ['PHYSICAL', 'VIRTUAL'];
export const RULE_TYPE_OPTIONS: LoyaltyRuleType[] = ['ACCRUAL', 'REDEMPTION', 'TIER_ASSIGNMENT'];
export const PROGRAM_MODEL_OPTIONS: LoyaltyProgramModel[] = ['BONUS', 'DISCOUNT', 'NONE'];
export const GENERATE_MODE_OPTIONS: GenerateMode[] = ['RANGE', 'MASK'];
export const CARD_POOL_STATUS_OPTIONS: CardPoolStatus[] = ['AVAILABLE', 'ISSUED'];
